import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FiChevronRight, FiGrid, FiList } from 'react-icons/fi';
import categoryService from '../../services/category.service';
import ProductCard from '../../components/product/ProductCard';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useLanguage } from '../../context/LanguageContext';

const CategoriesPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { t, lang } = useLanguage();
  const [tree, setTree] = useState([]);
  const [catData, setCatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const page = parseInt(searchParams.get('page')) || 1;

  // Load category tree for sidebar
  useEffect(() => {
    categoryService.getTree().then(r => setTree(r.data?.data || [])).catch(() => {});
  }, []);

  // Load specific category or show browse-all
  useEffect(() => {
    setLoading(true);
    if (slug) {
      categoryService.getBySlug(slug, { page, limit: 20 })
        .then(r => setCatData(r.data?.data || null))
        .catch(() => setCatData(null))
        .finally(() => setLoading(false));
    } else {
      setCatData(null);
      setLoading(false);
    }
  }, [slug, page]);

  const getName = (cat) => {
    if (lang !== 'en' && cat.translations?.[lang]?.name) return cat.translations[lang].name;
    return cat.name;
  };

  if (loading) return <LoadingSpinner />;

  // Browse all categories (no slug)
  if (!slug) {
    return (
      <div className="section-padding" style={{ minHeight: '80vh' }}>
        <div className="container">
          <h2 className="fw-bold mb-4">{t('browseCategories') || 'Browse Categories'}</h2>
          <div className="row g-3">
            {tree.map(cat => (
              <div key={cat._id} className="col-6 col-md-4 col-lg-3">
                <Link to={`/categories/${cat.slug}`} className="text-decoration-none">
                  <div className="dashboard-card text-center h-100" style={{ transition: 'transform 0.2s', cursor: 'pointer' }}
                    onMouseOver={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                    onMouseOut={e => e.currentTarget.style.transform = ''}>
                    <div style={{ fontSize: '40px', marginBottom: '8px' }}>{cat.icon || '📦'}</div>
                    <h6 className="fw-bold mb-1">{getName(cat)}</h6>
                    <small className="text-muted">{cat.productCount || 0} {t('products') || 'products'}</small>
                    {cat.children?.length > 0 && (
                      <div className="mt-2">
                        {cat.children.slice(0, 3).map(sub => (
                          <span key={sub._id} className="badge bg-light text-dark me-1 mb-1" style={{ fontSize: '11px' }}>
                            {getName(sub)}
                          </span>
                        ))}
                        {cat.children.length > 3 && (
                          <span className="text-muted" style={{ fontSize: '11px' }}>+{cat.children.length - 3} more</span>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Specific category view
  if (!catData) {
    return (
      <div className="section-padding text-center" style={{ minHeight: '60vh' }}>
        <h3>{t('categoryNotFound') || 'Category not found'}</h3>
        <Link to="/categories" className="btn btn-fd-primary mt-3">{t('browseCategories') || 'Browse Categories'}</Link>
      </div>
    );
  }

  const { category, breadcrumb, children, products, total, pages } = catData;

  return (
    <div className="section-padding" style={{ minHeight: '80vh' }}>
      <div className="container">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb" style={{ fontSize: '14px' }}>
            <li className="breadcrumb-item"><Link to="/">{t('home')}</Link></li>
            <li className="breadcrumb-item"><Link to="/categories">{t('categories') || 'Categories'}</Link></li>
            {breadcrumb.map((b, i) => (
              <li key={b.slug} className={`breadcrumb-item ${i === breadcrumb.length - 1 ? 'active' : ''}`}>
                {i === breadcrumb.length - 1
                  ? getName(b.name ? b : category)
                  : <Link to={`/categories/${b.slug}`}>{b.name}</Link>
                }
              </li>
            ))}
          </ol>
        </nav>

        <div className="row g-4">
          {/* Sidebar: subcategories */}
          <div className="col-lg-3 d-none d-lg-block">
            <div className="dashboard-card">
              <h6 className="fw-bold mb-3">{category.icon} {getName(category)}</h6>
              {children?.length > 0 && (
                <ul className="list-unstyled mb-0">
                  {children.map(sub => (
                    <li key={sub._id} className="mb-2">
                      <Link to={`/categories/${sub.slug}`}
                        className="text-decoration-none d-flex align-items-center justify-content-between text-dark">
                        <span>{sub.icon} {getName(sub)}</span>
                        <span className="badge bg-light text-muted">{sub.productCount || 0}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* All root categories */}
            <div className="dashboard-card mt-3">
              <h6 className="fw-bold small text-muted text-uppercase mb-2">{t('allCategories') || 'All Categories'}</h6>
              <ul className="list-unstyled mb-0" style={{ fontSize: '13px' }}>
                {tree.map(cat => (
                  <li key={cat._id} className="mb-1">
                    <Link to={`/categories/${cat.slug}`}
                      className={`text-decoration-none ${cat.slug === slug ? 'fw-bold text-primary' : 'text-dark'}`}>
                      {cat.icon} {getName(cat)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Products */}
          <div className="col-lg-9">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div>
                <h4 className="fw-bold mb-0">{category.icon} {getName(category)}</h4>
                <small className="text-muted">{total} {t('products') || 'products'}</small>
              </div>
              <div className="d-flex gap-2">
                <button className={`btn btn-sm ${viewMode === 'grid' ? 'btn-dark' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('grid')}><FiGrid /></button>
                <button className={`btn btn-sm ${viewMode === 'list' ? 'btn-dark' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('list')}><FiList /></button>
              </div>
            </div>

            {/* Subcategory chips (mobile + desktop) */}
            {children?.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mb-3">
                {children.map(sub => (
                  <Link key={sub._id} to={`/categories/${sub.slug}`}
                    className="btn btn-sm btn-outline-secondary rounded-pill">
                    {sub.icon} {getName(sub)}
                  </Link>
                ))}
              </div>
            )}

            {products.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">{t('noProductsInCategory') || 'No products in this category yet.'}</p>
              </div>
            ) : (
              <>
                <div className="row g-3">
                  {products.map(p => (
                    <div key={p._id} className={viewMode === 'grid' ? 'col-6 col-md-4' : 'col-12'}>
                      <ProductCard product={p} />
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {pages > 1 && (
                  <div className="d-flex justify-content-center mt-4">
                    <nav>
                      <ul className="pagination">
                        {Array.from({ length: pages }, (_, i) => (
                          <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                            <Link to={`/categories/${slug}?page=${i + 1}`} className="page-link">{i + 1}</Link>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
